import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import map from 'lodash/map'
import formattedCreditsWithHours from '@Utils/FormattedCreditsWithHours'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'
import isValidDottedDate from '@Utils/IsValidDottedDate'
import MultiselectionDropdown from '@SiteComponents/MultiselectionDropdown'
import Button from '@SiteComponents/Button'
import DottedDateInput, { type IDottedDate } from '@SiteComponents/DottedDateInput'
import StatisticsChart from '@SiteComponents/StatisticsChart'
import ThirtyDays from '@Images/statistics_30_days.svg'
import OtherPeriods from '@Images/statistics_other_periods.svg'
import Credits from '@Images/statistics_credits.svg'
import Projects from '@Images/statistics_projects.svg'
import User from '@Images/statistics_user.svg'
import Workspace from '@Images/statistics_workspace.svg'

import {
  getCreditUsage,
  getProjectCreation,
} from '@SiteModules/Statistics/actions'

import {
  getAccountUsers,
  getHasLoadedInitially,
} from '@SiteModules/AccountUser/selectors'

import {
  getCurrentAccountId,
} from '@SiteModules/Account/selectors'

import {
  getWorkspaces,
} from '@SiteModules/Workspace/selectors'

import {
  getCreditUsageSumNonAnalysis,
  getCreditUsageSumAnalysis,
  getProjectCreationSum,
  getCreditUsageDataNonAnalysis,
  getCreditUsageDataAnalysis,
  getProjectCreationData,
} from '@SiteModules/Statistics/selectors'

import './Statistics.scss'

const SWITCH_WIDTH_THIRTY_DAYS = 92 // px from Statistics.scss
const SWITCH_WIDTH_SIX_MONTHS = 99 // px from Statistics.scss
const SWITCH_WIDTH_ONE_YEAR = 80 // px from Statistics.scss
const SWITCH_WIDTH_ALL = 60 // px from Statistics.scss
const SWITCH_WIDTH_CUSTOM = 90 // px from Statistics.scss
const SWITCH_WIDTH_CREDITS = 86 // px from Statistics.scss
const SWITCH_WIDTH_ANALYSIS_CREDITS = 140 // px from Statistics.scss
const SWITCH_WIDTH_PROJECTS = 92 // px from Statistics.scss
const RESET_DATE: IDottedDate = {
  day: '',
  month: '',
  year: ''
}

const Statistics: React.FC = () => {
  const dispatch = useDispatch()

  const accountUsers = useSelector(getAccountUsers)
  const workspaces = useSelector(getWorkspaces)
  const currentAccountId = useSelector(getCurrentAccountId)
  const creditUsageSumNonAnalysis = useSelector(getCreditUsageSumNonAnalysis)
  const creditUsageSumAnalysis = useSelector(getCreditUsageSumAnalysis)
  const projectCreationSum = useSelector(getProjectCreationSum)
  const creditUsageDataNonAnalysis = useSelector(getCreditUsageDataNonAnalysis)
  const creditUsageDataAnalysis = useSelector(getCreditUsageDataAnalysis)
  const projectCreationData = useSelector(getProjectCreationData)
  const hasLoadedAccountUsersInitially = useSelector(getHasLoadedInitially)

  const [creditsOrProjects, setCreditsOrProjects] = useState('credits')
  const [period, setPeriod] = useState('30_days')
  const [selectedAccountUserIndices, setSelectedAccountUserIndices] = useState<number[]>(map(accountUsers, (_, index) => index))
  const [selectedWorkspaceIndices, setSelectedWorkspaceIndices] = useState<number[]>(map(workspaces, (_, index) => index))
  const [dateSelectorOpen, setDateSelectorOpen] = useState(false)
  const [customCounter, setCustomCounter] = useState(0)

  const creditsActive = creditsOrProjects === 'credits'
  const analysisCreditsActive = creditsOrProjects === 'analysis_credits'
  const projectsActive = creditsOrProjects === 'projects'
  const sixMonthsActive = period === '6_months'
  const oneYearActive = period === '1_year'
  const allActive = period === 'all'
  const customActive = period === 'custom' || dateSelectorOpen

  const [startDate, setStartDate] = useState<IDottedDate>(RESET_DATE)

  const [endDate, setEndDate] = useState<IDottedDate>(RESET_DATE)

  const startDateSet = isValidDottedDate(startDate).valid
  const endDateSet = isValidDottedDate(endDate).valid

  const sortedAccountUsers = [...accountUsers].sort((a, b) =>
    a.attributes.email.localeCompare(b.attributes.email)
  )

  const sortedWorkspaces = [...workspaces].sort((a, b) =>
    a.attributes.name.localeCompare(b.attributes.name)
  )

  const accountUserNames = map(sortedAccountUsers, accountUser => accountUser.attributes.name.length > 0 ? accountUser.attributes.name : accountUser.attributes.email)
  const accountUserEmails = map(sortedAccountUsers, accountUser => accountUser.attributes.email)
  const workspaceNames = map(sortedWorkspaces, workspace => workspace.attributes.name)

  useEffect(() => {
    selectAllAccountUsersAndWorkspaces()
  }, [hasLoadedAccountUsersInitially])

  useEffect(() => {
    getStatistics()
  }, [selectedAccountUserIndices, selectedWorkspaceIndices, customCounter, period])

  useEffect(() => {
    if (!dateSelectorOpen) {
      if (!startDateSet) {
        setStartDate(RESET_DATE)
      }

      if (!endDateSet) {
        setEndDate(RESET_DATE)
      }
    }
  }, [dateSelectorOpen])

  useClickOutsideListener('Statistics__date-selector', setDateSelectorOpen, dateSelectorOpen)

  const dottedToIsoDate = (date: IDottedDate): string => {
    const pad = (s: string, len: number) => s.padStart(len, '0')
    return `${pad(date.year, 4)}-${pad(date.month, 2)}-${pad(date.day, 2)}`
  }

  const selectAllAccountUsersAndWorkspaces = () => {
    setSelectedAccountUserIndices(map(sortedAccountUsers, (_, index) => index))
    setSelectedWorkspaceIndices(map(sortedWorkspaces, (_, index) => index))
  }

  const getStatistics = () => {
    const selectedAccountUsers = selectedAccountUserIndices.length > 0 ? map(selectedAccountUserIndices, i => sortedAccountUsers[i]) : undefined
    const selectedWorkspaces = selectedWorkspaceIndices.length > 0 ? map(selectedWorkspaceIndices, i => sortedWorkspaces[i]) : undefined

    const payload = {
      accountId: currentAccountId,
      period,
      ...((selectedAccountUsers) ? { userIds: map(selectedAccountUsers, selectedAccountUser => selectedAccountUser.attributes.userId) } : {}),
      ...((selectedWorkspaces) ? { workspaceIds: map(selectedWorkspaces, selectedWorkspace => selectedWorkspace.id) } : {}),
      ...((startDateSet) ? { startDate: dottedToIsoDate(startDate) } : {}),
      ...((endDateSet) ? { endDate: dottedToIsoDate(endDate) } : {})
    }
    dispatch(getCreditUsage(payload))
    dispatch(getProjectCreation(payload))
  }

  const renderHeader = () => {
    return (
      <div className="Statistics__header">
        Track your platform usage in real-time
      </div>
    )
  }

  const setThirtyDaysActive = () => {
    setPeriod('30_days')
  }

  const setSixMonthsActive = () => {
    setPeriod('6_months')
  }

  const setOneYearActive = () => {
    setPeriod('1_year')
  }

  const setCustomActive = () => {
    setPeriod('custom')
    setCustomCounter(customCounter + 1)
  }

  const setAllActive = () => {
    setPeriod('all')
  }

  const setCreditsActive = () => {
    setCreditsOrProjects('credits')
  }

  const setAnalysisCreditsActive = () => {
    setCreditsOrProjects('analysis_credits')
  }

  const setProjectsActive = () => {
    setCreditsOrProjects('projects')
  }

  const openDateSelector = () => {
    setDateSelectorOpen(true)
  }

  const renderDateSelector = () => {
    return (
      <div className="Statistics__date-selector">
        <div className="Statistics__date-selector-header">
          Start Date
        </div>
        <DottedDateInput
          value={startDate}
          onChange={setStartDate}
          className="Statistics__date-selector-date-picker"
        />
        <div className="Statistics__date-selector-header">
          End Date
        </div>
        <DottedDateInput
          value={endDate}
          onChange={setEndDate}
          className="Statistics__date-selector-date-picker"
        />
        <Button
          styleType="brand"
          size="large"
          caption="Apply"
          onClick={setCustomActive}
          disabled={!startDateSet || !endDateSet}
        />
      </div>
    )
  }

  let activeIndicatorPeriodSwitchWidth = SWITCH_WIDTH_THIRTY_DAYS
  let activeIndicatorPeriodSwitchPosition = 0
  if (sixMonthsActive) {
    activeIndicatorPeriodSwitchWidth = SWITCH_WIDTH_SIX_MONTHS
    activeIndicatorPeriodSwitchPosition = SWITCH_WIDTH_THIRTY_DAYS
  } else if (oneYearActive) {
    activeIndicatorPeriodSwitchWidth = SWITCH_WIDTH_ONE_YEAR
    activeIndicatorPeriodSwitchPosition = SWITCH_WIDTH_THIRTY_DAYS + SWITCH_WIDTH_SIX_MONTHS
  } else if (allActive) {
    activeIndicatorPeriodSwitchWidth = SWITCH_WIDTH_ALL
    activeIndicatorPeriodSwitchPosition = SWITCH_WIDTH_THIRTY_DAYS + SWITCH_WIDTH_SIX_MONTHS + SWITCH_WIDTH_ONE_YEAR
  } else if (customActive) {
    activeIndicatorPeriodSwitchWidth = SWITCH_WIDTH_CUSTOM
    activeIndicatorPeriodSwitchPosition = SWITCH_WIDTH_THIRTY_DAYS + SWITCH_WIDTH_SIX_MONTHS + SWITCH_WIDTH_ONE_YEAR + SWITCH_WIDTH_ALL
  }

  const renderPeriodSwitches = () => {
    return (
      <div className="Statistics__switches-wrapper">
        <ul className="Statistics__switches">
          <div
            className="Statistics__switches-active-indicator-wrapper"
            style={{
              transform: `translateX(${activeIndicatorPeriodSwitchPosition}px)`
            }}
          >
            <div
              className="Statistics__switches-active-indicator Statistics__switches-active-indicator-period"
              style={{
                width: `${activeIndicatorPeriodSwitchWidth}px`
              }}
            />
          </div>
          <li
            className="Statistics__switch Statistics__switch-thirty-days"
            onClick={setThirtyDaysActive}
          >
            <ThirtyDays/>
            30 Days
          </li>
          <li
            className="Statistics__switch Statistics__switch-six-months"
            onClick={setSixMonthsActive}
          >
            <OtherPeriods/>
            6 Months
          </li>
          <li
            className="Statistics__switch Statistics__switch-one-year"
            onClick={setOneYearActive}
          >
            <OtherPeriods/>
            1 Year
          </li>
          <li
            className="Statistics__switch Statistics__switch-all"
            onClick={setAllActive}
          >
            <OtherPeriods/>
            All
          </li>
          <li
            className="Statistics__switch Statistics__switch-custom"
            onClick={openDateSelector}
          >
            <OtherPeriods/>
            Custom
          </li>
        </ul>
        {dateSelectorOpen && renderDateSelector()}
      </div>
    )
  }

  const renderDropdowns = () => {
    return (
      <div className="Statistics__dropdowns">
        <div
          className="Statistics__dropdown-user-wrapper"
          id="statistics-dropdown-user-wrapper"
        >
          <MultiselectionDropdown
            availableOptions={accountUserNames}
            availableOptionDescriptions={accountUserEmails}
            selectedOptionIndices={selectedAccountUserIndices}
            entityName="user"
            icon={<User/>}
            classNameForClickOutsideListener="Statistics__dropdown-user"
            setSelectedOptionIndices={setSelectedAccountUserIndices}
            classNameOfContainerToMakeUnscrollable="Statistics"
            disabled={accountUserNames.length === 0}
            idNameOfParentContainer="statistics-dropdown-user-wrapper"
          />
        </div>
        <div
          className="Statistics__dropdown-workspace-wrapper"
          id="statistics-dropdown-workspace-wrapper"
        >
          <MultiselectionDropdown
            availableOptions={workspaceNames}
            selectedOptionIndices={selectedWorkspaceIndices}
            entityName="workspace"
            icon={<Workspace/>}
            classNameForClickOutsideListener="Statistics__dropdown-workspace"
            setSelectedOptionIndices={setSelectedWorkspaceIndices}
            classNameOfContainerToMakeUnscrollable="Statistics"
            disabled={workspaces.length === 0}
            idNameOfParentContainer="statistics-dropdown-workspace-wrapper"
          />
        </div>
      </div>
    )
  }

  const renderSummary = () => {
    return (
      <div className="Statistics__summary">
        <div className="Statistics__summary-item">
          <div className="Statistics__summary-item-header">
            Credits used
          </div>
          <div className="Statistics__summary-item-description">
            Number of credits used over the selected period
          </div>
          <div className="Statistics__summary-item-result">
            {formattedCreditsWithHours(creditUsageSumNonAnalysis)}
          </div>
        </div>
        <div className="Statistics__summary-item">
          <div className="Statistics__summary-item-header">
            Analysis credits used
          </div>
          <div className="Statistics__summary-item-description">
            Number of analysis credits used over the selected period
          </div>
          <div className="Statistics__summary-item-result">
            {formattedCreditsWithHours(creditUsageSumAnalysis)}
          </div>
        </div>
        <div className="Statistics__summary-item">
          <div className="Statistics__summary-item-header">
            Projects created
          </div>
          <div className="Statistics__summary-item-description">
            Number of projects created over the selected period
          </div>
          <div className="Statistics__summary-item-result">
            {projectCreationSum} projects
          </div>
        </div>
      </div>
    )
  }

  let activeIndicatorCreditsOrProjectsSwitchWidth = 0
  let activeIndicatorCreditsOrProjectsSwitchPosition = 0

  if (creditsActive) {
    activeIndicatorCreditsOrProjectsSwitchWidth = SWITCH_WIDTH_CREDITS
    activeIndicatorCreditsOrProjectsSwitchPosition = 0
  } else if (analysisCreditsActive) {
    activeIndicatorCreditsOrProjectsSwitchWidth = SWITCH_WIDTH_ANALYSIS_CREDITS
    activeIndicatorCreditsOrProjectsSwitchPosition = SWITCH_WIDTH_CREDITS
  } else if (projectsActive) {
    activeIndicatorCreditsOrProjectsSwitchWidth = SWITCH_WIDTH_PROJECTS
    activeIndicatorCreditsOrProjectsSwitchPosition = SWITCH_WIDTH_CREDITS + SWITCH_WIDTH_ANALYSIS_CREDITS
  }

  const renderCreditsOrProjectsSwitch = () => {
    return (
      <ul className="Statistics__switches">
        <div
          className="Statistics__switches-active-indicator-wrapper"
          style={{
            transform: `translateX(${activeIndicatorCreditsOrProjectsSwitchPosition}px)`
          }}
        >
          <div
            className="Statistics__switches-active-indicator"
            style={{
              width: `${activeIndicatorCreditsOrProjectsSwitchWidth}px`
            }}
          />
        </div>
        <li
          className="Statistics__switch Statistics__switch-credits"
          onClick={setCreditsActive}
        >
          <Credits/>
          Credits
        </li>
        <li
          className="Statistics__switch Statistics__switch-analysis-credits"
          onClick={setAnalysisCreditsActive}
        >
          <Credits/>
          Analysis credits
        </li>
        <li
          className="Statistics__switch Statistics__switch-projects"
          onClick={setProjectsActive}
        >
          <Projects/>
          Projects
        </li>
      </ul>
    )
  }

  const renderChart = () => {
    return (
      <React.Fragment>
        {creditsActive && (
          <StatisticsChart
            data={creditUsageDataNonAnalysis}
            valueKey="credits"
            label="Credits"
          />
        )}
        {analysisCreditsActive && (
          <StatisticsChart
            data={creditUsageDataAnalysis}
            valueKey="credits"
            label="Analysis credits"
          />
        )}
        {projectsActive && (
          <StatisticsChart
            data={projectCreationData}
            valueKey="projectsCount"
            label="Projects"
          />
        )}
      </React.Fragment>
    )
  }

  return (
    <div className="Statistics">
      <div className="Statistics__main">
        {renderHeader()}
        {renderPeriodSwitches()}
        {renderDropdowns()}
        {renderSummary()}
        {renderCreditsOrProjectsSwitch()}
        {renderChart()}
      </div>
    </div>
  )
}

export default Statistics
